import { useCallback } from 'react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { fileService } from '../services/fileService';
import { useFileStore } from '../store/fileStore';
import { useTaxYearStore } from '@/features/tax-year/store/taxYearStore';
import { MAX_FILE_SIZE, ALLOWED_FILE_TYPES } from '../types/file.types';
import { handleApiError } from '@/lib/utils/errorHandler';

export const useFileUpload = () => {
  const {
    startUpload,
    updateUploadProgress,
    completeUpload,
    failUpload,
    clearUpload,
  } = useFileStore();
  const effectiveYear = useTaxYearStore((s) => s.getEffectiveYear());

  const validateFile = useCallback((file: File): string | null => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `File "${file.name}" exceeds maximum size of 10MB`;
    }

    // Check file type
    const allowedTypes = Object.keys(ALLOWED_FILE_TYPES);
    if (!allowedTypes.includes(file.type)) {
      return `File type "${file.type}" is not allowed`;
    }

    return null;
  }, []);

  const uploadFile = useCallback(
    async (file: File): Promise<{ success: boolean; documentId?: string }> => {
      if (effectiveYear == null) {
        toast.error('Tax year is not ready yet. Please try again.');
        return { success: false };
      }

      // Validate file
      const validationError = validateFile(file);
      if (validationError) {
        toast.error(validationError);
        return { success: false };
      }

      const fileId = uuidv4();
      startUpload(fileId, file.name);

      try {
        const result = await fileService.uploadFile(
          file,
          effectiveYear,
          (progress) => {
            updateUploadProgress(fileId, progress);
          }
        );

        completeUpload(fileId);
        toast.success(`"${file.name}" uploaded successfully`);

        // Clear completed upload after delay
        setTimeout(() => {
          clearUpload(fileId);
        }, 3000);

        return { success: true, documentId: result.documentId };
      } catch (error) {
        const apiError = handleApiError(error);
        failUpload(fileId, apiError.message);
        return { success: false };
      }
    },
    [validateFile, startUpload, updateUploadProgress, completeUpload, failUpload, clearUpload, effectiveYear]
  );

  const uploadFiles = useCallback(
    async (files: File[]): Promise<{ successful: number; failed: number }> => {
      let successful = 0;
      let failed = 0;

      for (const file of files) {
        const result = await uploadFile(file);
        if (result.success) {
          successful++;
        } else {
          failed++;
        }
      }

      return { successful, failed };
    },
    [uploadFile]
  );

  return {
    uploadFile,
    uploadFiles,
    validateFile,
  };
};
